import { Role } from "../types/role";
import * as jose from "jose";
import { captureException } from "@sentry/core";
import { JWTPayload } from "jose";
import { DateTime, Duration, DurationLikeObject } from "luxon";

export const ACCESS_TOKEN_DURATION: DurationLikeObject = { days: 1 };
export const REFRESH_TOKEN_DURATION: DurationLikeObject = { days: 14 };
export const SESSION_TOKEN_DURATION: DurationLikeObject = { days: 90 };

export const LOGIN_QUERY_PARAMS = {
  CODE: "c",
  EMAIL: "e",
  PHONE: "p",
  METHOD: "m",
  REDIRECT: "r",
} as const;
export type LoginQueryParams = (typeof LOGIN_QUERY_PARAMS)[keyof typeof LOGIN_QUERY_PARAMS];

const secretToBuffer = (secret?: string): Buffer =>
  Buffer.from(secret ?? process.env.JWT_SECRET!, "base64");

type BaseTokenInfo<R extends Role = Role> = {
  activeRole: R;
  roles: R[];
  userId?: string;
  phone?: string;
  email?: string;
} & Pick<JWTPayload, "iat" | "exp"> &
  (R extends "CUSTOMER"
    ? { customerId: string }
    : {
        customerId?: string;
      });

/** URL query param keys recognized as tracking parameters, captured at session creation. */
export const TRACKING_PARAM_KEYS = [
  // UTM
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_term",
  "utm_content",
  // Click IDs
  "gclid",
  "wbraid",
  "gbraid",
  "fbclid",
  "ttclid",
  "msclkid",
  "MSCLKID",
  "OutbrainClickId",
  "TCLID",
  "click_id",
  // Everflow affiliate
  "oid",
  "affid",
  "sub1",
  "sub2",
  "sub3",
  "sub4",
  "sub5",
  "uid",
  "source_id",
  // Everflow advertiser
  "adv1",
  "adv2",
  "adv3",
  "adv4",
  "adv5",
] as const;

export type TrackingParamKey = (typeof TRACKING_PARAM_KEYS)[number];
export type TrackingParams = Partial<Record<TrackingParamKey, string>> & {
  /**
   * Meta `_fbc`-formatted value (`fb.1.{timestampMs}.{fbclid}`) synthesized at
   * session creation time when only a raw `fbclid` URL param is available.
   * Not extracted from the URL — derived from `fbclid` and stamped with the
   * click timestamp so downstream consumers (Everflow `adv5`, Meta CAPI) get
   * the canonical format. The raw `fbclid` stays alongside it in this blob
   * for forensics.
   */
  fbc?: string;
};

export type SessionTokenInfo<R extends Role = Role> = BaseTokenInfo<R> & {
  customerId: string;
  trackingParams?: TrackingParams;
};

export type TokenInfo<R extends Role = Role> = BaseTokenInfo<R> &
  (
    | {
        userId?: never;
        phone?: string;
        email?: string;
      }
    | {
        userId: string;
        email: string;
        phone?: string;
      }
    | {
        userId: string;
        email?: string;
        phone: string;
      }
  );

export type RefreshTokenInfo<R extends Role = Role> = TokenInfo<R> & {
  tokenId: string;
};

export const signJwt = <T extends Record<string, unknown> = TokenInfo>(
  tokenInfo: T,
  exp: DurationLikeObject,
  /**
   * Override for Cypress, since it doesn't have process.env.JWT_SECRET
   */
  secret?: string
): Promise<string> => {
  if (typeof window !== "undefined" && typeof window.Cypress === "undefined") {
    throw new Error("Cannot sign JWTs in the browser"); // Secret is only available server-side
  }
  return new jose.SignJWT(tokenInfo)
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt()
    .setExpirationTime(DateTime.utc().plus(Duration.fromObject(exp)).toJSDate())
    .sign(secretToBuffer(secret));
};

export const verifyJwt = async <T extends Record<string, unknown>>(
  jwt: string | undefined
): Promise<T | undefined> => {
  if (typeof window !== "undefined" && typeof window.Cypress === "undefined") {
    throw new Error("Cannot verify JWTs in the browser"); // Secret is only available server-side
  }
  try {
    return jwt ? (await jose.jwtVerify<T>(jwt, secretToBuffer())).payload : undefined;
  } catch (e) {
    handleJwtError(e);
  }
};

export const decodeJwt = <T extends Record<string, unknown>>(
  jwt: string | undefined
): T | undefined => {
  try {
    return jwt ? jose.decodeJwt<T>(jwt) : undefined;
  } catch (e) {
    handleJwtError(e);
  }
};

export function handleJwtError(e: Error) {
  if (
    !(e instanceof jose.errors.JWSSignatureVerificationFailed) &&
    !(e instanceof jose.errors.JWTExpired) &&
    !(e instanceof jose.errors.JWTInvalid) &&
    !(e instanceof jose.errors.JWSInvalid)
  ) {
    captureException(e);
  }
}
